import { useLocation, useNavigate } from '@muse-web/router'
import { getSignInUrl, getSignUpUrl } from '@core/routes'
import { useActions } from '@core/store'
import { useUploadFlow } from '@features/audio-upload'
import { useShowTrySubscriptionButton } from '@features/subscription'
import { useCurrentUser } from '@features/session'
import { ROUTES } from '../constants/routes'
import { headerStore } from '../store/headerStore'

/**
 * Shared header action logic used by both the desktop header actions and the mobile drawer footer:
 * auth-derived flags, the sign-in / try-free targets, and the upload handler. Keeps the two surfaces
 * from drifting. `id === 0` is a guest. Explicit "log in" / upload from the header navigate to the
 * full-page `/auth/sign-in` (parity with audio-web `goToAuthPage`); the floating auth modal is only
 * for in-action guest gates (follow/like/download/comment/HQ), not the header.
 */
export const useHeaderActions = () => {
    const { pathname, search } = useLocation()
    const navigate = useNavigate()
    const { id } = useCurrentUser()
    const { closeMobileMenu } = useActions({ closeMobileMenu: headerStore.actions.closeMobileMenu })
    const showTryFree = useShowTrySubscriptionButton()
    const { openFilePicker } = useUploadFlow()

    const isAuthenticated = id !== 0
    const returnUrl = pathname + search
    const signInUrl = getSignInUrl(returnUrl)

    const onSignIn = () => {
        closeMobileMenu()
        navigate(signInUrl)
    }

    const onUpload = () => {
        if (!isAuthenticated) {
            closeMobileMenu()
            navigate(signInUrl)

            return
        }

        // Before closing the drawer: the picker has to open inside this click, or the browser blocks it.
        openFilePicker('header')
        closeMobileMenu()
    }

    return {
        isAuthenticated,
        showTryFree,
        signInUrl,
        onSignIn,
        tryFreeHref: isAuthenticated ? ROUTES.subscriptions : getSignUpUrl(),
        closeMobileMenu,
        onUpload,
    }
}
